import { Form, Input, message, Space, Typography } from 'antd'
import api from 'api'
import { Button } from 'components/shared/Button'
import { Loading } from 'components/shared/Loading'
import colors from 'helpers/colors'
import { useOwner } from 'hooks/owners'
import { useProperties } from 'hooks/static/useProperties'
import { useTranslation } from 'next-i18next'
import { FC, useState } from 'react'

import { CustomPropertyItem } from '../properties/CustomPropertyItem'
import { AddressForm } from './AddressForm'

const OBJECT_TYPE = 'customer'

type Props = {
  ownerId: string
  initialValues?: Partial<Customer>
  xs?: boolean
  onSuccess: () => void
}

export const CustomerForm: FC<Props> = ({
  ownerId,
  initialValues,
  xs,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const { data: owner } = useOwner({ id: ownerId })
  const { t } = useTranslation()

  const { data: properties, isValidating: isValidatingProperties } =
    useProperties(ownerId, OBJECT_TYPE)
  const [newPropertySelections, setNewPropertySelections] = useState<
    {
      propertyId: string
      id?: string
      value: PropertyValue
    }[]
  >([])

  const [addressExist, setAddressExist] = useState(false)

  const onFinish = async (values: any) => {
    const { data, error }: { data: any; error: any } = await api.post(
      '/customers',
      {
        ...values,
        property_values: newPropertySelections.map((v) => {
          return {
            property_id: v.propertyId,
            value: v.value,
            id: v.id,
          }
        }),
        owner_id: ownerId,
      },
    )
    if (data) {
      onSuccess()
    }
    if (error) {
      message.error(error.data.error)
    }
  }

  const onFinishFailed = () => {
    message.error(t('入力内容に不備があります'))
  }

  const handleChangeProperties = (id: string, value: any) => {
    setNewPropertySelections((prev) => {
      if (prev.map((v) => v.propertyId).includes(id)) {
        return prev.map((v) => {
          if (v.propertyId === id) {
            v.value = value
          }
          return v
        })
      }

      return [...prev, { propertyId: id, value }]
    })
  }

  return (
    <>
      <Typography.Title
        style={{ paddingBottom: '24px', whiteSpace: 'pre-line' }}
      >
        {owner?.name} {xs && '\n'}
        {t('ご来場登録')}
      </Typography.Title>
      <Form
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        form={form}
        initialValues={initialValues}
      >
        <div
          style={{
            width: '100%',
            paddingBottom: '8px',
            marginBottom: '24px',
            borderBottom: `1px solid ${colors.grey}`,
          }}
        >
          <Typography.Text style={{ fontSize: 16 }}>
            {t('必須項目')}
          </Typography.Text>
        </div>

        <Form.Item label={t('姓名')} style={{ marginBottom: 0 }}>
          <Form.Item
            name="last_name"
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            rules={[
              {
                required: true,
                message: t('姓を入力してください'),
              },
            ]}
          >
            <Input placeholder={t('姓')} />
          </Form.Item>
          <Form.Item
            name="first_name"
            style={{
              display: 'inline-block',
              width: '50%',
              margin: '0 0 0 8px',
            }}
            rules={[
              {
                required: true,
                message: t('名を入力してください'),
              },
            ]}
          >
            <Input placeholder={t('名')} />
          </Form.Item>
        </Form.Item>

        <Form.Item label={t('フリガナ')} style={{ marginBottom: 0 }}>
          <Form.Item
            name="last_name_kana"
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
          >
            <Input placeholder={t('セイ')} />
          </Form.Item>

          <Form.Item
            name="first_name_kana"
            style={{
              display: 'inline-block',
              width: '50%',
              margin: '0 0 0 8px',
            }}
          >
            <Input placeholder={t('メイ')} />
          </Form.Item>
        </Form.Item>
        <Form.Item label={t('メールアドレス')} name="email">
          <Typography.Text>{initialValues?.email}</Typography.Text>
        </Form.Item>

        <Form.Item
          name={['phone_numbers_attributes', '0', 'phone_number']}
          label={t('電話番号')}
          rules={[{ required: true, message: t('必須項目です') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name={['phone_numbers_attributes', '0', 'label']} hidden />
        <Form.Item
          name={['phone_numbers_attributes', '0', 'is_default']}
          hidden
        />

        <Form.Item>
          <Button type="primary" onClick={() => form.submit()}>
            {t('送信する')}
          </Button>
        </Form.Item>

        <div
          className="formSpace"
          style={{ display: 'block', height: '24px' }}
        />

        <div
          style={{
            width: '100%',
            paddingBottom: '8px',
            margin: '24px 0',
            borderBottom: `1px solid ${colors.grey}`,
          }}
        >
          <Typography.Text style={{ fontSize: 16 }}>
            {t('任意項目')}
          </Typography.Text>
        </div>

        <Form.Item label={t('住所')}>
          <Space
            direction="vertical"
            style={{ width: xs ? '-webkit-fill-available' : '800px' }}
          >
            <Button
              type="secondary"
              style={{ display: 'inline-block' }}
              size="md"
              onClick={() => setAddressExist((value) => !value)}
            >
              {addressExist ? t('住所を削除する') : t('住所を入力する')}
            </Button>
            {addressExist && <AddressForm form={form} />}
          </Space>
        </Form.Item>

        <Form.Item label={t('会社名')} name="company_name">
          <Input placeholder={t('〇〇株式会社')} />
        </Form.Item>

        <Form.Item label={t('部署')} name="company_branch">
          <Input />
        </Form.Item>

        <Form.Item label={t('役職')} name="company_position">
          <Input />
        </Form.Item>

        <Form.Item label={t('興味関心')} name="interest">
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>

        <Form.Item label={t('その他')} name="memo">
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>

        <Form.Item label="Name" style={{ marginBottom: 0 }}>
          <Form.Item
            name="first_name_en"
            style={{
              display: 'inline-block',
              width: 'calc(33% - 8px)',
              marginRight: '8px',
            }}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Form.Item
            name="middle_name"
            style={{
              display: 'inline-block',
              width: 'calc(33% - 8px)',
              marginRight: '8px',
            }}
          >
            <Input placeholder="Middle Name" />
          </Form.Item>
          <Form.Item
            name="last_name_en"
            style={{ display: 'inline-block', width: '34%' }}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
        </Form.Item>

        <Form.Item label="Company Name" name="company_name_en">
          <Input placeholder="〇〇 Inc." />
        </Form.Item>

        <Form.Item label="Department" name="company_branch_en">
          <Input />
        </Form.Item>

        <Form.Item label="Position" name="company_position_en">
          <Input />
        </Form.Item>

        <Form.Item label="Interest" name="interest_en">
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>

        <Form.Item label="Others" name="memo_en">
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>

        {isValidatingProperties ? (
          <Loading />
        ) : (
          <>
            {properties &&
              properties.map((property) => {
                const newPropertySelection = newPropertySelections.find(
                  (v) => v.propertyId === property.id,
                )
                return (
                  <Form.Item key={property.id} label={property.name}>
                    <CustomPropertyItem
                      key={property.id}
                      property={property}
                      value={newPropertySelection?.value[property.field_type]}
                      onChangeProperties={handleChangeProperties}
                    />
                  </Form.Item>
                )
              })}
          </>
        )}

        <Form.Item>
          <Button type="primary" onClick={() => form.submit()}>
            {t('送信する')}
          </Button>
        </Form.Item>
      </Form>
    </>
  )
}
